import { type SearchClient, type SearchIndex } from 'algoliasearch';
import { type GetObjectsOptions, type SearchOptions } from '@algolia/client-search';
import { type RequestOptions } from '@algolia/transporter';
import { createAlgoliaClient } from '@libs/algolia';
import { logger } from '@libs/core';
import type { ProductInterfaceAlgolia } from '@libs/common-types';

export class AlgoliaProductService {
    readonly client: SearchClient;
    index: SearchIndex;

    constructor(indexName: string) {
        this.client = createAlgoliaClient({ admin: true });
        this.index = this.client.initIndex(indexName);
    }

    async testConnection(): Promise<boolean> {
        logger.info(
            `[testConnection] Algolia index: ${this.index.indexName}, App ID: ${process.env.NEXT_PUBLIC_ALGOLIA_APP_ID}`
        );
        try {
            await this.index.getSettings();
            logger.info(`[testConnection] Successfully connected to Algolia.`);
            return true;
        } catch (error) {
            if (error instanceof Error) {
                logger.error(`[testConnection] Error connecting to Algolia: ${error.message}`, {
                    error,
                });
            } else {
                logger.error(`[testConnection] Error connecting to Algolia: ${error}`);
            }
            return false;
        }
    }

    async fetchAllProducts(searchOptions: SearchOptions = {}): Promise<ProductInterfaceAlgolia[]> {
        if (!(await this.testConnection())) {
            throw new Error('[fetchAllProducts] Failed to connect to Algolia');
        }

        const products: ProductInterfaceAlgolia[] = [];
        try {
            await this.index.browseObjects({
                ...searchOptions,
                ...{
                    batch: batch => {
                        products.push(...(batch as ProductInterfaceAlgolia[]));
                    },
                },
            });
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`[fetchAllProducts] Error fetching all products: ${error.message}`);
            } else {
                logger.error('[fetchAllProducts] Error:', error);
                throw new Error(`[fetchAllProducts] Unknown error occurred`);
            }
        }
        return products;
    }

    async fetchProductsByIds(
        ids: string[],
        requestOptions?: RequestOptions & GetObjectsOptions
    ): Promise<ProductInterfaceAlgolia[]> {
        try {
            logger.info('IDS', ids);
            const { results } = await this.index.getObjects<ProductInterfaceAlgolia>(ids, requestOptions);
            return results.filter((product): product is ProductInterfaceAlgolia => product !== null);
        } catch (error) {
            if (error instanceof Error) {
                logger.error(`[fetchProductsByIds] Error fetching products by IDs: ${error.message}`, {
                    error,
                });
                throw new Error(`[fetchProductsByIds] Error fetching products by IDs: ${error.message}`);
            } else {
                logger.error('[fetchProductsByIds] Error:', error);
                throw new Error(`[fetchProductsByIds] Unknown error occurred`);
            }
        }
    }

    async updateProducts(products: ProductInterfaceAlgolia[]): Promise<void> {
        try {
            await this.index.partialUpdateObjects(products, {
                createIfNotExists: true,
            });
        } catch (error) {
            if (error instanceof Error) {
                logger.error(`[updateProducts] Error updating products: ${error.message}`, {
                    error,
                });
                throw new Error(`[updateProducts] Error updating products: ${error.message}`);
            } else {
                logger.error('[updateProducts] Error:', error);
                throw new Error(`[updateProducts] Unknown error occurred`);
            }
        }
    }
}
